import { storageKeyForMode } from "../publicGuestMode";

export const AUTH_SESSION_KEY = storageKeyForMode("invoice-studio-auth", "invoice-studio-public-guest-auth-v1");

export const PROTOTYPE_IDENTITY_CAPABILITIES = Object.freeze({
  mode: "prototype",
  productionReady: false,
  sessionAuthority: "browser-storage",
  credentialVerification: "fixture-account-map",
  serverRevocation: false,
  accountRecovery: false,
  multiDeviceSessions: false,
});

const storagePair = () => {
  if (typeof window === "undefined") return { local: null, session: null };
  return { local: window.localStorage, session: window.sessionStorage };
};

const validSession = (session, workspaceIds) => Boolean(
  session
  && typeof session.organizationId === "string"
  && session.organizationId.trim()
  && (
    (session.mode === "local" && (!session.localSpace || ["sample", "portal-sample", "custom", "standard", "empty"].includes(session.localSpace)))
    || (session.mode === "account" && workspaceIds.has(session.organizationId) && session.email)
  )
);

export const createPrototypeIdentityRepository = ({ workspaces, loginAccounts, getStorage = storagePair }) => {
  const workspaceIds = new Set(workspaces.map((workspace) => workspace.id));

  const locateSession = () => {
    const stores = getStorage();
    for (const [persistence, storage] of [["local", stores.local], ["session", stores.session]]) {
      if (!storage) continue;
      try {
        const session = JSON.parse(storage.getItem(AUTH_SESSION_KEY));
        if (validSession(session, workspaceIds)) return { session, persistence, storage };
      } catch {
        // Malformed or unavailable prototype storage is treated as signed out.
      }
    }
    return null;
  };

  const writeSession = (session, persistence) => {
    const stores = getStorage();
    const primary = persistence === "session" ? stores.session : stores.local;
    const secondary = persistence === "session" ? stores.local : stores.session;
    if (!primary) return { ok: false, error: "Browser storage is unavailable on this device." };
    try {
      secondary?.removeItem(AUTH_SESSION_KEY);
      primary.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
      return { ok: true, session };
    } catch {
      return { ok: false, error: "The prototype session could not be saved in this browser." };
    }
  };

  const authenticateAccount = ({ email, password, remember }) => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!/^\S+@\S+\.\S+$/.test(normalizedEmail)) return { ok: false, error: "Enter a valid work email." };
    if (password.length < 6) return { ok: false, error: "Enter a password with at least 6 characters." };
    const organizationId = loginAccounts[normalizedEmail];
    if (!organizationId || !workspaceIds.has(organizationId)) return { ok: false, error: "The email or password is not recognized." };
    return writeSession(
      { organizationId, email: normalizedEmail, mode: "account" },
      remember ? "local" : "session",
    );
  };

  const startLocalSession = (organizationId, localSpace = "") => {
    if (typeof organizationId !== "string" || !organizationId.trim()) return { ok: false, error: "The local organization is not available." };
    if (localSpace && !["sample", "portal-sample", "custom", "standard", "empty"].includes(localSpace)) return { ok: false, error: "The local workspace type is not recognized." };
    return writeSession({ organizationId, mode: "local", ...(localSpace ? { localSpace } : {}) }, "local");
  };

  const replaceOrganization = (organizationId) => {
    if (typeof organizationId !== "string" || !organizationId.trim()) return { ok: false, error: "The restored organization is not available." };
    const located = locateSession();
    if (!located) return { ok: false, error: "No active prototype session could be updated." };
    if (located.session.mode === "account" && !workspaceIds.has(organizationId)) {
      return { ok: false, error: "An account session cannot switch to an organization it is not assigned to." };
    }
    return writeSession({ ...located.session, organizationId }, located.persistence);
  };

  const signOut = () => {
    const stores = getStorage();
    try {
      stores.local?.removeItem(AUTH_SESSION_KEY);
      stores.session?.removeItem(AUTH_SESSION_KEY);
      return { ok: true };
    } catch {
      return { ok: false, error: "The prototype session could not be cleared from this browser." };
    }
  };

  return Object.freeze({
    capabilities: PROTOTYPE_IDENTITY_CAPABILITIES,
    readSession: () => locateSession()?.session || null,
    authenticateAccount,
    startLocalSession,
    replaceOrganization,
    signOut,
  });
};
